import React, { useState, useEffect, createRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Swiper from 'swiper';
import { Card } from '../components';
import { Icon } from '../elements';
import 'swiper/swiper-bundle.css';
import { setActiveType } from '~/store/modules/home/actions';

const MainContainer = styled.div`
  width: 100%;
  max-width: 411px;
  display: flex;
  justify-content: center;
  padding: 0;
  margin-top: -25px;
  margin-left: -40px;

  @media (max-width: ${({ theme }) => theme.devices.tablet}) {
    width: 100%;
    max-width: initial;
    margin-left: 0;
  }

  @media (max-width: 560px) {
    overflow: hidden;
  }

  .swiper-container {
    width: 100%;
  }

  .mobile-cards-wrapper {
    width: 100%;
    max-width: 311px;
    box-sizing: border-box;
  }
  .swiper-card-container {
    width: 100%;
    height: 260px;
    .swiper-wrapper {
      display: flex;
      align-items: center;
    }
    .swiper-slide {
      display: flex;
      justify-content: center;
      height: 205px !important;
      width: 311px !important;
    }
  }
`;

const Container = styled.div`
  position: relative;
  width: 311px;
  height: 205px;
  margin-top: 30px;
  padding: 0 44px;
  box-sizing: content-box;

  button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 40px;
    width: 40px;
    background: ${({ theme }) => theme.colors.white};
    border-radius: 100%;
    box-shadow: 0px 0px 8px #00000022;
    z-index: 5;
    border: 0;
    color: ${({ theme }) => theme.colors.primary};
    outline: none;
    font-size: 16px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:active {
      background: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.white};
    }

    &[disabled] {
      color: ${({ theme }) => theme.colors.disabledColor};
      opacity: 0.9;
    }

    &.prev {
      left: 0;
      cursor: pointer;
    }
    &.next {
      right: 0;
      cursor: pointer;
    }
  }
`;

const Slide = styled.div`
  position: absolute;
  pointer-events: none;
  top: 0;
  left: 50px;
  opacity: 0;
  transform: translate(12px, 15px);
  transition: 1s;
  z-index: 2;

  &.active {
    opacity: 1;
    pointer-events: initial;
    transform: translate(0, 0);
    z-index: 2;
  }

  &.next {
    opacity: 1;
    transform: translate(-12px, -15px);
    z-index: 1;
  }

  &.sub-next {
    opacity: 0;
    transform: translate(-24px, -30px);
    z-index: 0;
  }
`;

export const Desktop = ({ activeSlide, onChange, slides }) => {
  const getClass = i => {
    if (activeSlide === i) {
      return 'active';
    }
    if (activeSlide === i - 1) {
      return 'next';
    }
    if (activeSlide === i - 2) {
      return 'sub-next';
    }

    return '';
  };

  return (
    <MainContainer>
      <Container className="card-step">
        {slides.length > 1 && (
          <button
            aria-label="Cartão anterior"
            type="button"
            disabled={activeSlide === 0}
            onClick={() => onChange(activeSlide - 1)}
            className="prev"
          >
            <Icon icon={['fas', 'chevron-left']} />
          </button>
        )}
        {[
          ...slides,
          slides.length > 1 ? slides[0] : null,
          slides.length > 2 ? slides[1] : null,
        ].map(
          (card, i) =>
            card && (
              <Slide key={`${card}${i}`} className={getClass(i)}>
                <Card
                  caixa={card.tipo}
                  type={card.codigoProduto}
                  data={card}
                  blocked={card.statusCartao !== 3}
                />
              </Slide>
            ),
        )}
        {slides.length > 1 && (
          <button
            aria-label="Próximo cartão"
            disabled={activeSlide + 1 === slides.length}
            type="button"
            onClick={() => onChange(activeSlide + 1)}
            className="next"
          >
            <Icon icon={['fas', 'chevron-right']} />
          </button>
        )}
      </Container>
    </MainContainer>
  );
};

export const Mobile = ({ activeSlide, onChange, slides }) => {
  const [cardSwiper, setCardSwiper] = useState();
  const cardSwiperEl = createRef();
  const dispatch = useDispatch();

  useEffect(() => {
    if (slides?.length > 0) {
      const activeType = slides?.[activeSlide];
      dispatch(setActiveType(activeType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cardSwiperEl && !cardSwiper) {
      setCardSwiper(
        new Swiper('.swiper-card-container', {
          direction: 'horizontal',
          loop: false,
          spaceBetween: 10,
          initialSlide: activeSlide,
        }),
      );
    }
    // eslint-disable-next-line
  }, [cardSwiperEl]);

  useEffect(() => {
    if (cardSwiper) {
      cardSwiper.on('slideChangeTransitionStart', s => {
        setTimeout(() => {
          onChange(s.activeIndex);
        }, 10);
      });
    }
    // eslint-disable-next-line
  }, [cardSwiper]);

  useEffect(() => {
    const activeType = slides?.[activeSlide];

    if (cardSwiper) {
      cardSwiper.slideTo(activeSlide);
      dispatch(setActiveType(activeType));
    }
    // eslint-disable-next-line
  }, [activeSlide]);

  return (
    <MainContainer
      firstSlide={activeSlide === 0}
      lastSlide={activeSlide === slides.length - 1}
    >
      <div className="mobile-cards-wrapper card-step">
        <div className="swiper-card-container" ref={cardSwiperEl}>
          <div className="swiper-wrapper">
            {slides.map((card, i) => (
              <div className="swiper-slide" key={i}>
                <Card
                  caixa={card.tipo}
                  type={card.codigoProduto}
                  data={card}
                  blocked={card.statusCartao !== 3}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

Mobile.propTypes = {
  activeSlide: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  slides: PropTypes.array.isRequired,
};

Desktop.propTypes = {
  activeSlide: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  slides: PropTypes.array.isRequired,
};

export default {
  Desktop,
  Mobile,
};
